import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useErrorHandler, ClientError, useQueryClient, useJobStatus, JobIdResponse } from 'shared-ui';
import { toast } from 'react-toastify';

import { editGroupTerminalPermission } from 'api/permissions/permissions';
import { AppMessages } from 'i18n/messages';
import { QueryKeyEnum } from 'core/global.enum';
import { useLocale } from 'hooks/useLocale/useLocale';
import { useSelectedGroupTerminalPermission } from 'hooks/useSelectedGroupTerminalPermission/useSelectedGroupTerminalPermission';
import { useMutation } from 'hooks/useMutation/useMutation';

import { GroupTerminalPermissionDetails } from './GroupTerminalPermissionDetails';
import {
  GroupTerminalPermissionDetailsContainerProps,
  GroupTerminalPermissionDetailsEditForm,
} from './GroupTerminalPermissionDetails.types';
import { useGroupTerminalPermissionDetailsValidation } from './GroupTerminalPermissionDetails.validation';

const isJobResponse = (response: JobIdResponse | void): response is JobIdResponse => {
  return response !== undefined && response !== null && typeof response === 'object' && 'jobId' in response;
};

export const GroupTerminalPermissionDetailsContainer = ({
  selectedPermission,
  groupId,
}: GroupTerminalPermissionDetailsContainerProps) => {
  const { formatMessage } = useLocale();
  const { setSelectedGroupTerminalPermission } = useSelectedGroupTerminalPermission();
  const queryClient = useQueryClient();
  const { schema } = useGroupTerminalPermissionDetailsValidation();
  const { handleError } = useErrorHandler();

  const jobStatus = useJobStatus({
    autoClose: false,
    useGlobalModal: true,
    showItemCount: false,
    onComplete: () => {
      queryClient.invalidateQueries([QueryKeyEnum.GROUP_TERMINALS_PERMISSIONS_SERVERS]);
      queryClient.invalidateQueries([QueryKeyEnum.GROUP_TERMINALS_PERMISSIONS]);
      toast.dark(formatMessage({ id: AppMessages['group.reader.permission.details.edit.success'] }));
    },
    customCompleteMessage: formatMessage({ id: AppMessages['groups.result.success'] }),
    customFailedMessage: formatMessage({ id: AppMessages['groups.result.failed'] }),
    customActiveMessage: formatMessage({ id: AppMessages['groups.result.waiting'] }),
    title: formatMessage({ id: AppMessages['groups.result.title'] }),
  });

  const { mutate: onEditPermission, isLoading } = useMutation(editGroupTerminalPermission, {
    onSuccess: (response, { body }) => {
      if (isJobResponse(response)) {
        jobStatus.startJob(response.jobId);

        setSelectedGroupTerminalPermission({ ...selectedPermission, ...body });
      } else {
        setSelectedGroupTerminalPermission({ ...selectedPermission, ...body });
        queryClient.invalidateQueries([QueryKeyEnum.GROUP_TERMINALS_PERMISSIONS_SERVERS]);
        queryClient.invalidateQueries([QueryKeyEnum.GROUP_TERMINALS_PERMISSIONS]);
        toast.dark(formatMessage({ id: AppMessages['group.reader.permission.details.edit.success'] }));
      }
    },
    onError: (error: ClientError<Error>) => {
      handleError({ error });
    },
  });

  const formMethods = useForm<GroupTerminalPermissionDetailsEditForm>({
    mode: 'all',
    resolver: yupResolver(schema),
    defaultValues: {
      otwierajZamek: selectedPermission.otwierajZamek,
      rejestrujOdbicie: selectedPermission.rejestrujOdbicie,
      rejestrujRcp: selectedPermission.rejestrujRcp,
    },
  });

  useEffect(() => {
    formMethods.reset(selectedPermission);
  }, [selectedPermission]);

  const onSubmit = (body: GroupTerminalPermissionDetailsEditForm) => {
    onEditPermission({
      groupId: groupId,
      permissionId: selectedPermission.id,
      body: { idTkd: selectedPermission.tkd.id, ...body },
    });
  };

  return (
    <FormProvider {...formMethods}>
      <GroupTerminalPermissionDetails
        isLoading={isLoading}
        selectedPermission={selectedPermission}
        onSubmit={onSubmit}
      />
    </FormProvider>
  );
};
